import { useCallback, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sparkles, Loader2, Palette } from 'lucide-react';
import { ThemeSelector } from './ThemeSelector';
import { getThemeById } from '@fias/arche-sdk';
import type { FiasTheme } from '@fias/arche-sdk';

interface PluginGuidedWelcomeProps {
  onStart: (idea: string, name?: string) => void;
  isProcessing: boolean;
  idea: string;
  onIdeaChange: (idea: string) => void;
  name: string;
  onNameChange: (name: string) => void;
  selectedThemeId: string;
  onThemeChange: (themeId: string, customTheme?: FiasTheme) => void;
  selectedFontPairingId: string;
  onFontPairingChange: (fontPairingId: string) => void;
}

function ThemeChooserButton({
  selectedThemeId,
  onThemeChange,
  selectedFontPairingId,
  onFontPairingChange,
  disabled,
}: {
  selectedThemeId: string;
  onThemeChange: (themeId: string, customTheme?: FiasTheme) => void;
  selectedFontPairingId: string;
  onFontPairingChange: (fontPairingId: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const entry = getThemeById(selectedThemeId);
  const themeName = entry?.name ?? 'Custom';
  const isDefault = selectedThemeId === 'fias-dark';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className="w-full flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
      >
        <Palette className="h-3.5 w-3.5 shrink-0" />
        <span>
          Theme: <span className="text-foreground font-medium">{themeName}</span>
          {isDefault && <span className="text-muted-foreground"> (default)</span>}
          {' \u2014 '}
          <span className="underline underline-offset-2">Change</span>
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl h-[85vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>Choose a Theme</DialogTitle>
          </DialogHeader>
          <ThemeSelector
            selectedThemeId={selectedThemeId}
            onThemeChange={(id, ct) => {
              onThemeChange(id, ct);
            }}
            selectedFontPairingId={selectedFontPairingId}
            onFontPairingChange={onFontPairingChange}
            className="flex-1 min-h-0"
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

export function PluginGuidedWelcome({
  onStart,
  isProcessing,
  idea,
  onIdeaChange,
  name,
  onNameChange,
  selectedThemeId,
  onThemeChange,
  selectedFontPairingId,
  onFontPairingChange,
}: PluginGuidedWelcomeProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  /** Capitalize the first letter of each word */
  const toTitleCase = (value: string) => value.replace(/\b\w/g, (char) => char.toUpperCase());

  const handleSubmit = () => {
    const trimmed = idea.trim();
    if (!trimmed || isProcessing) return;
    const finalName = name.trim() ? toTitleCase(name.trim()) : undefined;
    onStart(trimmed, finalName);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-6 gap-6 max-w-lg mx-auto overflow-y-auto">
      <div className="text-center space-y-2">
        <Sparkles className="h-8 w-8 mx-auto text-primary" />
        <h2 className="text-xl font-semibold">What Arche would you like to build?</h2>
        <p className="text-sm text-muted-foreground">
          Describe your idea and we'll guide you through the entire build process.
        </p>
      </div>

      <div className="w-full space-y-1">
        <label htmlFor="plugin-name" className="text-sm font-medium">
          Arche name (optional)
        </label>
        <Input
          id="plugin-name"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="My Arche"
          disabled={isProcessing}
        />
      </div>

      <Textarea
        ref={textareaRef}
        value={idea}
        onChange={(e) => {
          onIdeaChange(e.target.value);
          autoResize();
        }}
        onKeyDown={handleKeyDown}
        placeholder="Put the description of the Arche you would like to build here. Please give as much detail as possible."
        className="min-h-[100px] resize-none overflow-hidden"
        disabled={isProcessing}
        autoFocus
      />

      <ThemeChooserButton
        selectedThemeId={selectedThemeId}
        onThemeChange={onThemeChange}
        selectedFontPairingId={selectedFontPairingId}
        onFontPairingChange={onFontPairingChange}
        disabled={isProcessing}
      />

      <Button
        onClick={handleSubmit}
        disabled={!idea.trim() || isProcessing}
        className="w-full"
        size="lg"
      >
        {isProcessing ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Sparkles className="mr-2 h-4 w-4" />
        )}
        Start Building
      </Button>
    </div>
  );
}
